import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Home, Search, Radio, Video, MessageCircle, Bell, Plus, User,
  Heart, MessageSquare, Share2, Bookmark, MoreHorizontal, Play,
  Sparkles, Flame, ArrowRight, X, Image as ImageIcon, Film, Camera,
  ChevronLeft, ChevronRight
} from 'lucide-react';

// ============================================
// SAMPLE DATA
// ============================================
const MOMENTS_DATA = [
  { 
    id: 'm1', 
    user: 'Anastasia', 
    image: 'https://images.unsplash.com/photo-1618331835717-801e976710b2?w=400&h=600&fit=crop',
    isLive: true,
    viewers: 2341
  },
  { 
    id: 'm2', 
    user: 'Marcus', 
    image: 'https://images.unsplash.com/photo-1541961017774-22349e4a1262?w=400&h=600&fit=crop',
    isNew: true
  },
  { 
    id: 'm3', 
    user: 'Yuki', 
    image: 'https://images.unsplash.com/photo-1595550891383-152fbed0f9bc?w=400&h=600&fit=crop',
    isNew: true
  },
  { 
    id: 'm4', 
    user: 'Lena', 
    image: 'https://images.unsplash.com/photo-1533392151650-269f96231f65?w=400&h=600&fit=crop',
  },
  { 
    id: 'm5', 
    user: 'Kai', 
    image: 'https://images.unsplash.com/photo-1601597565151-70c4020dc0e1?w=400&h=600&fit=crop',
  },
];

const FEED_POSTS = [
  {
    id: 'p1',
    type: 'featured',
    user: { name: 'Anastasia Voss', handle: '@anastasia', avatar: 'https://images.unsplash.com/photo-1595550891383-152fbed0f9bc?w=150' },
    image: 'https://images.unsplash.com/photo-1602128110234-2d11c0aaadfe?w=1200',
    title: 'The Architecture of Silence',
    caption: 'Finding poetry in concrete and light. The city speaks in shadows.',
    likes: 4892,
    comments: 234,
    timeAgo: '2h'
  },
  {
    id: 'p2',
    type: 'quote',
    user: { name: 'Marcus Chen', handle: '@marcus', avatar: 'https://images.unsplash.com/photo-1533392151650-269f96231f65?w=150' },
    quote: '"The future belongs to those who believe in the beauty of their dreams."',
    likes: 1203,
    comments: 89,
    timeAgo: '4h'
  },
  {
    id: 'p3',
    type: 'image',
    user: { name: 'Yuki Tanaka', handle: '@yuki', avatar: 'https://images.unsplash.com/photo-1601597565151-70c4020dc0e1?w=150' },
    image: 'https://images.pexels.com/photos/26588746/pexels-photo-26588746.jpeg?w=800',
    caption: 'Urban geometries',
    likes: 2341,
    comments: 156,
    timeAgo: '6h'
  },
  {
    id: 'p4',
    type: 'image',
    user: { name: 'Lena Okonkwo', handle: '@lena', avatar: 'https://images.unsplash.com/photo-1611695434369-a8f5d76ceb7b?w=150' },
    image: 'https://images.pexels.com/photos/14528986/pexels-photo-14528986.jpeg?w=800',
    caption: 'Chasing light through glass corridors',
    likes: 3567,
    comments: 201,
    timeAgo: '8h'
  },
];

const LIVE_STREAMS = [
  {
    id: 'live1',
    host: { name: 'Anastasia', avatar: 'https://images.unsplash.com/photo-1595550891383-152fbed0f9bc?w=150' },
    title: 'Live: Studio Session',
    viewers: 2341,
    thumbnail: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=600&h=400&fit=crop',
    category: 'Creative'
  },
  {
    id: 'live2',
    host: { name: 'Marcus', avatar: 'https://images.unsplash.com/photo-1533392151650-269f96231f65?w=150' },
    title: 'Q&A: Design Philosophy',
    viewers: 1567,
    thumbnail: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=400&fit=crop',
    category: 'Talk'
  },
];

// ============================================
// COMPONENTS
// ============================================

// Floating Navigation (Desktop)
const FloatingNav = ({ activeTab, onTabChange, onCreateClick, canGoBack, canGoForward, onBack, onForward }) => {
  return (
    <motion.nav 
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="fixed top-6 left-1/2 -translate-x-1/2 z-50 hidden lg:flex items-center gap-1 px-2 py-2 rounded-full bg-white/80 backdrop-blur-xl border border-black/5 shadow-lg shadow-black/5"
    >
      {/* Back/Forward Buttons */}
      <div className="flex items-center gap-1 mr-2 pr-2 border-r border-black/10">
        <button
          onClick={onBack}
          disabled={!canGoBack}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
            canGoBack 
              ? 'text-[#121212] hover:bg-black/5 active:scale-95' 
              : 'text-[#121212]/30 cursor-not-allowed'
          }`}
          data-testid="nav-back"
          title="Go back"
        >
          <ChevronLeft className="w-5 h-5" strokeWidth={1.5} />
        </button>
        <button
          onClick={onForward}
          disabled={!canGoForward}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
            canGoForward 
              ? 'text-[#121212] hover:bg-black/5 active:scale-95' 
              : 'text-[#121212]/30 cursor-not-allowed'
          }`}
          data-testid="nav-forward"
          title="Go forward"
        >
          <ChevronRight className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>

      {[
        { id: 'home', icon: Home, label: 'Feed' },
        { id: 'explore', icon: Search, label: 'Explore' },
        { id: 'live', icon: Radio, label: 'Live' },
        { id: 'messages', icon: MessageCircle, label: 'Messages', badge: 3 },
        { id: 'notifications', icon: Bell, label: 'Activity' },
      ].map(item => {
        const Icon = item.icon;
        const isActive = activeTab === item.id;
        return (
          <button
            key={item.id}
            onClick={() => onTabChange(item.id)}
            className={`relative flex items-center gap-2 px-4 py-2.5 rounded-full transition-all duration-300 ${
              isActive ? 'bg-[#121212] text-white' : 'text-[#121212] hover:bg-black/5'
            }`}
            data-testid={`nav-${item.id}`}
          >
            <Icon className="w-4 h-4" strokeWidth={1.5} />
            <span className="text-sm font-medium">{item.label}</span>
            {item.badge && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#CE422B] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {item.badge}
              </span>
            )}
          </button>
        );
      })}
      
      {/* Create Button */}
      <button
        onClick={onCreateClick}
        className="ml-2 w-10 h-10 rounded-full bg-[#121212] text-white flex items-center justify-center hover:scale-105 transition-transform"
        data-testid="nav-create"
      >
        <Plus className="w-5 h-5" strokeWidth={2} />
      </button>
    </motion.nav>
  );
};

// Mobile Bottom Navigation
const MobileNav = ({ activeTab, onTabChange, onCreateClick }) => {
  return (
    <nav className="lg:hidden fixed bottom-4 left-4 right-4 z-50">
      <div className="flex items-center justify-around py-3 px-4 rounded-2xl bg-white/90 backdrop-blur-xl border border-black/5 shadow-lg">
        {[
          { id: 'home', icon: Home },
          { id: 'explore', icon: Search },
          { id: 'create', icon: Plus, isCreate: true },
          { id: 'messages', icon: MessageCircle, badge: 3 },
          { id: 'profile', icon: User },
        ].map(item => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          
          if (item.isCreate) {
            return (
              <button
                key={item.id}
                onClick={onCreateClick}
                className="w-12 h-12 rounded-xl bg-[#121212] text-white flex items-center justify-center -mt-8 shadow-lg"
                data-testid="mobile-create"
              >
                <Icon className="w-6 h-6" strokeWidth={2} />
              </button>
            );
          }
          
          return (
            <button
              key={item.id}
              onClick={() => onTabChange(item.id)}
              className={`relative p-2 ${isActive ? 'text-[#121212]' : 'text-[#6B6B6B]'}`}
              data-testid={`mobile-nav-${item.id}`}
            >
              <Icon className="w-6 h-6" strokeWidth={isActive ? 2 : 1.5} />
              {item.badge && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#CE422B] text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                  {item.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

// Editorial Header
const EditorialHeader = ({ user, canGoBack, canGoForward, onBack, onForward }) => {
  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 z-40 px-4 py-3 bg-white/90 backdrop-blur-xl border-b border-[#E5E5E5]">
      <div className="flex items-center justify-between">
        {/* Left: Back/Forward + Logo */}
        <div className="flex items-center gap-2">
          {/* Back/Forward Buttons */}
          <div className="flex items-center gap-0.5">
            <button
              onClick={onBack}
              disabled={!canGoBack}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                canGoBack 
                  ? 'text-[#121212] active:bg-black/5 active:scale-95' 
                  : 'text-[#121212]/30'
              }`}
              data-testid="mobile-back"
            >
              <ChevronLeft className="w-5 h-5" strokeWidth={2} />
            </button>
            <button
              onClick={onForward}
              disabled={!canGoForward}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                canGoForward 
                  ? 'text-[#121212] active:bg-black/5 active:scale-95' 
                  : 'text-[#121212]/30'
              }`}
              data-testid="mobile-forward"
            >
              <ChevronRight className="w-5 h-5" strokeWidth={2} />
            </button>
          </div>
          
          {/* Logo */}
          <Link to="/echo" className="flex items-center gap-1.5">
            <div className="w-7 h-7 rounded-lg bg-[#121212] flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-editorial text-xl font-light tracking-tight text-[#121212]">Echo</span>
          </Link>
        </div>
        
        {/* Right: Notifications + Profile */}
        <div className="flex items-center gap-3">
          <button className="relative" data-testid="mobile-notifications">
            <Bell className="w-5 h-5 text-[#121212]" strokeWidth={1.5} />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-[#CE422B] rounded-full" />
          </button>
          <div className="w-8 h-8 rounded-full overflow-hidden border border-[#E5E5E5]">
            <img src={user?.picture || 'https://images.unsplash.com/photo-1595550891383-152fbed0f9bc?w=100'} alt="" className="w-full h-full object-cover" />
          </div>
        </div>
      </div>
    </header>
  );
};

// Rectangular Moment Card (NOT circular like Instagram)
const MomentCard = ({ moment, index }) => {
  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="relative flex-shrink-0 w-28 sm:w-32 aspect-[3/4] rounded-lg overflow-hidden group"
      data-testid={`moment-${moment.id}`}
    >
      <img 
        src={moment.image} 
        alt={moment.user}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      
      {/* Live Badge */}
      {moment.isLive && (
        <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded bg-[#CE422B]">
          <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
          <span className="text-[10px] font-semibold text-white uppercase tracking-wide">Live</span>
        </div>
      )}
      
      {/* New Badge */}
      {moment.isNew && !moment.isLive && (
        <div className="absolute top-2 left-2 w-2 h-2 rounded-full bg-[#CE422B]" />
      )}
      
      {/* User Name */}
      <div className="absolute bottom-2 left-2 right-2">
        <p className="text-white text-sm font-medium truncate">{moment.user}</p>
        {moment.viewers && (
          <p className="text-white/70 text-xs">{moment.viewers.toLocaleString()} watching</p>
        )}
      </div>
    </motion.button>
  );
};

// Moments Section (Horizontal scroll of rectangular cards)
const MomentsSection = () => {
  return (
    <section className="py-6 lg:py-8">
      <div className="px-4 lg:px-0 mb-4 flex items-end justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6B6B6B] mb-1">Now</p>
          <h2 className="font-editorial text-2xl lg:text-3xl font-light text-[#121212] tracking-tight">Moments</h2>
        </div>
        <button className="flex items-center gap-1 text-sm text-[#121212] hover:gap-2 transition-all" data-testid="see-all-moments">
          See all <ArrowRight className="w-4 h-4" />
        </button>
      </div>
      
      <div className="flex gap-3 overflow-x-auto px-4 lg:px-0 pb-2 scrollbar-hide">
        {/* Add Moment Card */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative flex-shrink-0 w-28 sm:w-32 aspect-[3/4] rounded-lg overflow-hidden bg-[#F0F0ED] border border-dashed border-[#121212]/20 flex flex-col items-center justify-center gap-2 hover:border-[#121212]/40 transition-colors"
          data-testid="add-moment"
        >
          <div className="w-10 h-10 rounded-full bg-[#121212] flex items-center justify-center">
            <Plus className="w-5 h-5 text-white" />
          </div>
          <span className="text-xs font-medium text-[#6B6B6B]">Add Moment</span>
        </motion.button>
        
        {MOMENTS_DATA.map((moment, i) => (
          <MomentCard key={moment.id} moment={moment} index={i + 1} />
        ))}
      </div>
    </section>
  );
};

// Featured Post (Large editorial style)
const FeaturedPost = ({ post }) => {
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-xl lg:rounded-2xl"
      data-testid={`post-${post.id}`}
    >
      {/* Image */}
      <div className="relative aspect-[4/5] lg:aspect-[16/9]">
        <img 
          src={post.image} 
          alt={post.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
        
        {/* Content Overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-10">
          {/* User */}
          <div className="flex items-center gap-3 mb-4">
            <img src={post.user.avatar} alt="" className="w-10 h-10 rounded-full border-2 border-white/30" />
            <div>
              <p className="text-white font-medium">{post.user.name}</p>
              <p className="text-white/60 text-sm">{post.user.handle} · {post.timeAgo}</p>
            </div>
          </div>
          
          {/* Title & Caption */}
          <h3 className="font-editorial text-3xl lg:text-5xl font-light text-white mb-2 tracking-tight">{post.title}</h3>
          <p className="text-white/80 text-base lg:text-lg max-w-xl">{post.caption}</p>
          
          {/* Actions */}
          <div className="flex items-center gap-6 mt-6">
            <button 
              onClick={() => setLiked(!liked)}
              className="flex items-center gap-2 text-white hover:scale-105 transition-transform"
              data-testid="post-like"
            >
              <Heart className={`w-6 h-6 ${liked ? 'fill-[#CE422B] text-[#CE422B]' : ''}`} strokeWidth={1.5} />
              <span className="text-sm font-medium">{(post.likes + (liked ? 1 : 0)).toLocaleString()}</span>
            </button>
            <button className="flex items-center gap-2 text-white hover:scale-105 transition-transform" data-testid="post-comment">
              <MessageSquare className="w-6 h-6" strokeWidth={1.5} />
              <span className="text-sm font-medium">{post.comments}</span>
            </button>
            <button className="flex items-center gap-2 text-white hover:scale-105 transition-transform" data-testid="post-share">
              <Share2 className="w-6 h-6" strokeWidth={1.5} />
            </button>
            <button 
              onClick={() => setSaved(!saved)}
              className="ml-auto text-white hover:scale-105 transition-transform"
              data-testid="post-save"
            >
              <Bookmark className={`w-6 h-6 ${saved ? 'fill-white' : ''}`} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </div>
    </motion.article>
  );
};

// Quote Post (Typography focused)
const QuotePost = ({ post }) => {
  const [liked, setLiked] = useState(false);
  
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#F8F8F6] rounded-xl lg:rounded-2xl p-8 lg:p-12"
      data-testid={`post-${post.id}`}
    >
      <div className="flex items-center gap-3 mb-8">
        <img src={post.user.avatar} alt="" className="w-10 h-10 rounded-full" />
        <div>
          <p className="text-[#121212] font-medium">{post.user.name}</p>
          <p className="text-[#6B6B6B] text-sm">{post.timeAgo}</p>
        </div>
      </div>
      
      <blockquote className="font-editorial text-2xl lg:text-4xl text-[#121212] leading-tight tracking-tight mb-8">
        {post.quote}
      </blockquote>
      
      <div className="flex items-center gap-6 pt-6 border-t border-[#E5E5E5]">
        <button 
          onClick={() => setLiked(!liked)}
          className="flex items-center gap-2 text-[#121212] hover:text-[#CE422B] transition-colors"
        >
          <Heart className={`w-5 h-5 ${liked ? 'fill-[#CE422B] text-[#CE422B]' : ''}`} strokeWidth={1.5} />
          <span className="text-sm">{(post.likes + (liked ? 1 : 0)).toLocaleString()}</span>
        </button>
        <button className="flex items-center gap-2 text-[#121212]">
          <MessageSquare className="w-5 h-5" strokeWidth={1.5} />
          <span className="text-sm">{post.comments}</span>
        </button>
        <button className="flex items-center gap-2 text-[#121212]">
          <Share2 className="w-5 h-5" strokeWidth={1.5} />
        </button>
      </div>
    </motion.article>
  );
};

// Standard Image Post
const ImagePost = ({ post }) => {
  const [liked, setLiked] = useState(false);
  
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      className="group"
      data-testid={`post-${post.id}`}
    >
      {/* Image */}
      <div className="relative aspect-square overflow-hidden rounded-xl lg:rounded-2xl mb-4">
        <img 
          src={post.image} 
          alt={post.caption}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      </div>
      
      {/* Content */}
      <div className="px-1">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <img src={post.user.avatar} alt="" className="w-9 h-9 rounded-full" />
            <div>
              <p className="text-[#121212] font-medium text-sm">{post.user.name}</p>
              <p className="text-[#6B6B6B] text-xs">{post.timeAgo}</p>
            </div>
          </div>
          <button className="text-[#6B6B6B] hover:text-[#121212]">
            <MoreHorizontal className="w-5 h-5" />
          </button>
        </div>
        
        <p className="text-[#121212] text-sm mb-3">{post.caption}</p>
        
        <div className="flex items-center gap-5">
          <button 
            onClick={() => setLiked(!liked)}
            className="flex items-center gap-1.5 text-[#121212]"
          >
            <Heart className={`w-5 h-5 ${liked ? 'fill-[#CE422B] text-[#CE422B]' : ''}`} strokeWidth={1.5} />
            <span className="text-sm">{(post.likes + (liked ? 1 : 0)).toLocaleString()}</span>
          </button>
          <button className="flex items-center gap-1.5 text-[#121212]">
            <MessageSquare className="w-5 h-5" strokeWidth={1.5} />
            <span className="text-sm">{post.comments}</span>
          </button>
          <button className="text-[#121212]">
            <Share2 className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </motion.article>
  );
};

// Bento Feed Grid (Magazine style - asymmetric)
const BentoFeed = () => {
  return (
    <section className="py-6 lg:py-10">
      <div className="px-4 lg:px-0 mb-6 flex items-end justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6B6B6B] mb-1">Curated</p>
          <h2 className="font-editorial text-2xl lg:text-3xl font-light text-[#121212] tracking-tight">Your Feed</h2>
        </div>
      </div>
      
      {/* Bento Grid - Asymmetric layout */}
      <div className="px-4 lg:px-0 space-y-6 lg:space-y-8">
        {/* Featured Large Post */}
        <FeaturedPost post={FEED_POSTS[0]} />
        
        {/* Two Column Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <QuotePost post={FEED_POSTS[1]} />
          <ImagePost post={FEED_POSTS[2]} />
        </div>
        
        {/* Single Image */}
        <ImagePost post={FEED_POSTS[3]} />
      </div>
    </section>
  );
};

// Live Now Section
const LiveSection = () => {
  return (
    <section className="py-6 lg:py-10 bg-[#F8F8F6]">
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        <div className="mb-6 flex items-end justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Radio className="w-6 h-6 text-[#121212]" />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-[#CE422B] rounded-full animate-pulse" />
            </div>
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6B6B6B] mb-0.5">Broadcasting</p>
              <h2 className="font-editorial text-2xl lg:text-3xl font-light text-[#121212] tracking-tight">Live Now</h2>
            </div>
          </div>
          <button className="px-4 py-2 rounded-full bg-[#121212] text-white text-sm font-medium hover:bg-[#121212]/90 transition-colors" data-testid="go-live">
            Go Live
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
          {LIVE_STREAMS.map((stream) => (
            <motion.div
              key={stream.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative overflow-hidden rounded-xl lg:rounded-2xl aspect-video cursor-pointer group"
              data-testid={`live-${stream.id}`}
            >
              <img src={stream.thumbnail} alt="" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              
              {/* Live Badge */}
              <div className="absolute top-4 left-4 flex items-center gap-2">
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-[#CE422B]">
                  <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                  <span className="text-[10px] font-bold text-white uppercase tracking-wide">Live</span>
                </span>
                <span className="px-2 py-1 rounded bg-black/50 text-white text-xs">
                  {stream.viewers.toLocaleString()} watching
                </span>
              </div>
              
              {/* Info */}
              <div className="absolute bottom-4 left-4 right-4 flex items-center gap-3">
                <img src={stream.host.avatar} alt="" className="w-10 h-10 rounded-full border-2 border-white/50" />
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{stream.host.name}</p>
                  <p className="text-white/70 text-sm truncate">{stream.title}</p>
                </div>
                <button className="px-4 py-2 rounded-full bg-white text-[#121212] text-sm font-semibold hover:bg-white/90 transition-colors">
                  Watch
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Create Modal
const CreateModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-white rounded-2xl w-full max-w-md overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          <div className="flex items-center justify-between p-4 border-b border-[#E5E5E5]">
            <span className="font-medium text-[#121212]">Create</span>
            <button onClick={onClose} className="p-1 hover:bg-black/5 rounded-full" data-testid="close-create">
              <X className="w-5 h-5 text-[#121212]" />
            </button>
          </div>
          
          <div className="p-6 grid grid-cols-2 gap-4">
            {[
              { id: 'post', icon: ImageIcon, label: 'Post', desc: 'Share a photo' },
              { id: 'moment', icon: Camera, label: 'Moment', desc: 'Share now' },
              { id: 'reel', icon: Film, label: 'Reel', desc: 'Short video' },
              { id: 'live', icon: Radio, label: 'Go Live', desc: 'Stream now' },
            ].map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  className="flex flex-col items-center p-6 rounded-xl border border-[#E5E5E5] hover:border-[#121212] hover:bg-[#F8F8F6] transition-all group"
                  data-testid={`create-${item.id}`}
                >
                  <div className="w-12 h-12 rounded-full bg-[#F0F0ED] group-hover:bg-[#121212] flex items-center justify-center mb-3 transition-colors">
                    <Icon className="w-5 h-5 text-[#121212] group-hover:text-white transition-colors" strokeWidth={1.5} />
                  </div>
                  <span className="font-medium text-[#121212] text-sm">{item.label}</span>
                  <span className="text-xs text-[#6B6B6B]">{item.desc}</span>
                </button>
              );
            })}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ============================================
// MAIN PAGE COMPONENT
// ============================================
export default function AiraEchoPageNew() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('home');
  const [showCreate, setShowCreate] = useState(false);
  
  // Navigation History State
  const [history, setHistory] = useState(['home']);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  // Check if can go back/forward
  const canGoBack = historyIndex > 0;
  const canGoForward = historyIndex < history.length - 1;
  
  // Handle tab change with history tracking
  const handleTabChange = useCallback((tab) => {
    if (tab === activeTab) return;
    
    // Add to history (remove any forward history)
    const newHistory = [...history.slice(0, historyIndex + 1), tab];
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setActiveTab(tab);
  }, [activeTab, history, historyIndex]);
  
  // Go back in history
  const handleBack = useCallback(() => {
    if (canGoBack) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setActiveTab(history[newIndex]);
    }
  }, [canGoBack, historyIndex, history]);
  
  // Go forward in history
  const handleForward = useCallback(() => {
    if (canGoForward) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setActiveTab(history[newIndex]);
    }
  }, [canGoForward, historyIndex, history]);

  const renderContent = () => {
    switch (activeTab) {
      case 'live':
        return (
          <div className="min-h-screen bg-white pt-16 lg:pt-24 pb-24 lg:pb-12">
            <LiveSection />
          </div>
        );
      case 'explore':
        return (
          <div className="min-h-screen bg-white pt-16 lg:pt-24 pb-24 lg:pb-12 flex items-center justify-center">
            <div className="text-center px-4">
              <Search className="w-16 h-16 mx-auto text-[#E5E5E5] mb-4" strokeWidth={1} />
              <h2 className="font-editorial text-3xl text-[#121212] mb-2">Explore</h2>
              <p className="text-[#6B6B6B]">Discover new creators and content</p>
            </div>
          </div>
        );
      case 'messages':
        return (
          <div className="min-h-screen bg-white pt-16 lg:pt-24 pb-24 lg:pb-12 flex items-center justify-center">
            <div className="text-center px-4">
              <MessageCircle className="w-16 h-16 mx-auto text-[#E5E5E5] mb-4" strokeWidth={1} />
              <h2 className="font-editorial text-3xl text-[#121212] mb-2">Messages</h2>
              <p className="text-[#6B6B6B]">Your conversations</p>
            </div>
          </div>
        );
      case 'notifications':
        return (
          <div className="min-h-screen bg-white pt-16 lg:pt-24 pb-24 lg:pb-12 flex items-center justify-center">
            <div className="text-center px-4">
              <Bell className="w-16 h-16 mx-auto text-[#E5E5E5] mb-4" strokeWidth={1} />
              <h2 className="font-editorial text-3xl text-[#121212] mb-2">Activity</h2>
              <p className="text-[#6B6B6B]">Your notifications</p>
            </div>
          </div>
        );
      case 'profile':
        return (
          <div className="min-h-screen bg-white pt-16 lg:pt-24 pb-24 lg:pb-12 flex items-center justify-center">
            <div className="text-center px-4">
              <div className="w-20 h-20 mx-auto rounded-full overflow-hidden mb-4 border border-[#E5E5E5]">
                <img src={user?.picture || 'https://images.unsplash.com/photo-1595550891383-152fbed0f9bc?w=200'} alt="" className="w-full h-full object-cover" />
              </div>
              <h2 className="font-editorial text-3xl text-[#121212] mb-2">{user?.name || 'Your Profile'}</h2>
              <p className="text-[#6B6B6B]">Your creative space</p>
            </div>
          </div>
        );
      default:
        return (
          <div className="min-h-screen bg-white pt-16 lg:pt-24 pb-24 lg:pb-12">
            <div className="max-w-6xl mx-auto">
              <MomentsSection />
              <BentoFeed />
              <LiveSection />
            </div>
          </div>
        );
    }
  };

  return (
    <div className="bg-white min-h-screen">
      {/* Desktop Floating Nav */}
      <FloatingNav 
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onCreateClick={() => setShowCreate(true)}
        canGoBack={canGoBack}
        canGoForward={canGoForward}
        onBack={handleBack}
        onForward={handleForward}
      />
      
      {/* Mobile Header */}
      <EditorialHeader 
        user={user}
        canGoBack={canGoBack}
        canGoForward={canGoForward}
        onBack={handleBack}
        onForward={handleForward}
      />
      
      {/* Main Content */}
      {renderContent()}
      
      {/* Mobile Bottom Nav */}
      <MobileNav 
        activeTab={activeTab}
        onTabChange={handleTabChange}
        onCreateClick={() => setShowCreate(true)}
      />
      
      {/* Create Modal */}
      <CreateModal isOpen={showCreate} onClose={() => setShowCreate(false)} />
    </div>
  );
}
